import React from "react";
import { Pie, PieChart as RechartsPieChart, Cell, Legend, Tooltip } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "../../ui/chart";
import { chartStyles } from "../chartStyles";
import { formatNumber } from "../utils";

interface CofDistribution {
  key: string;
  displayName: string;
  value: number;
  percentage: number;
}

interface PieChartProps {
  title: string;
  description?: string;
  data: CofDistribution[];
  year: number;
}

const COF_COLORS: Record<string, string> = {
  "very-high": "var(--cof-very-high)",
  high: "var(--cof-high)",
  moderate: "var(--cof-moderate)",
  low: "var(--cof-low)",
  "very-low": "var(--cof-very-low)",
  unknown: "var(--cof-unknown)",
};

export function PieChart({ title, description, data, year }: PieChartProps) {
  const filteredData = data.filter((item) => item.value > 0);

  const chartConfig = filteredData.reduce(
    (config, item) => {
      config[item.key] = {
        label: item.displayName,
        color: COF_COLORS[item.key] || "#6b7280",
      };
      return config;
    },
    {} as Record<string, { label: string; color: string }>,
  );

  const renderCustomLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }: {
    cx: number;
    cy: number;
    midAngle: number;
    innerRadius: number;
    outerRadius: number;
    percent: number;
  }) => {
    if (percent < 0.05) return null;

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const renderLegend = (props: {
    payload?: Array<{ value: string; color?: string }>;
  }): React.ReactNode => {
    const { payload } = props;
    return (
      <div className={chartStyles.legendContainer}>
        {payload?.map((entry, index) => {
          const item = filteredData.find((d) => d.key === entry.value);
          return (
            <div key={`legend-${index}`} className={chartStyles.legendItem}>
              <div
                className={chartStyles.swatch}
                style={{ backgroundColor: entry.color ?? "#6b7280" }}
              />
              <span>
                {item?.displayName}: {item && formatNumber(item.value)} ({item?.percentage}%)
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Card className="tw:h-full">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description || `Distribution for ${year}`}</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="tw:aspect-auto tw:h-[300px] tw:w-full">
          <RechartsPieChart>
            <Pie
              data={filteredData}
              dataKey="value"
              nameKey="key"
              cx="50%"
              cy="50%"
              outerRadius={100}
              labelLine={false}
              label={renderCustomLabel}
            >
              {filteredData.map((entry) => (
                <Cell key={`cell-${entry.key}`} fill={COF_COLORS[entry.key] || "#6b7280"} />
              ))}
            </Pie>
            <ChartTooltip
              content={
                <ChartTooltipContent
                  formatter={(value, name) => {
                    const item = filteredData.find((d) => d.key === name);
                    return [
                      `${formatNumber(Number(value))} (${item?.percentage}%) `,
                      item?.displayName,
                    ];
                  }}
                />
              }
            />
            <Legend content={renderLegend} />
          </RechartsPieChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
